/**
 * "You may also like" for the cart drawer.
 *
 * Ported from the live Dawn theme (assets/cart-drawer-recommendations.js). The
 * scoring below is Dawn's, reproduced so the two themes rank the same products
 * the same way; the fetching and rendering are rewritten, because Dawn's half
 * was bound to its own upsell markup.
 *
 * The idea worth keeping: recommendations are fetched for EVERY item in the
 * cart, not just one, and then merged. A product suggested by three of your
 * four cart items should beat one suggested by a single item, and asking about
 * only the first item throws that signal away.
 *
 *   Q  how much of the recommending items is in the cart   weight 0.30
 *   N  how many distinct cart items recommended it         weight 0.40
 *   P  how near the top of each list it appeared           weight 0.30
 *
 * Each is normalised to 0..1 before weighting, so a large cart does not
 * inflate the score of everything in it.
 */
(function () {
  'use strict';

  var W_QUANTITY = 0.3;
  var W_FREQUENCY = 0.4;
  var W_POSITION = 0.3;

  function parseCartItems(el) {
    try {
      var raw = el.getAttribute('data-cart-items');
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function fetchFor(el, productId, limit) {
    var url =
      el.getAttribute('data-url') +
      '?section_id=' +
      encodeURIComponent(el.getAttribute('data-section-id')) +
      '&product_id=' +
      encodeURIComponent(productId) +
      '&limit=' +
      encodeURIComponent(limit);

    return fetchCards(url);
  }

  /* Any URL that renders cart-recommendations-source: the Recommendations API
     above, or a collection page for the empty cart's fallback row.

     jsonKey is for the collection page. There `?section_id=` came back as an
     empty section wrapper -- no cards, though the same section through
     `?sections=` rendered all ten -- so the fallback asks with `sections=`,
     which answers JSON keyed by section id. */
  function fetchCards(url, jsonKey) {
    return fetch(url)
      .then(function (response) {
        if (!response.ok) return '';
        if (!jsonKey) return response.text();
        return response.json().then(function (data) {
          return (data && data[jsonKey]) || '';
        });
      })
      .then(function (html) {
        if (!html) return [];
        var holder = document.createElement('div');
        holder.innerHTML = html;
        var cards = holder.querySelectorAll('.cart-rec-card');
        var out = [];
        for (var i = 0; i < cards.length; i++) {
          var id = cards[i].getAttribute('data-rec-id');
          var position = parseInt(cards[i].getAttribute('data-rec-position'), 10);
          if (!id) continue;
          out.push({ id: String(id), position: position || i + 1, node: cards[i] });
        }
        return out;
      })
      .catch(function () {
        /* One source failing should not empty the row -- the other cart items
           can still carry it. Resolved as empty rather than rejected so
           Promise.all below does not collapse. */
        return [];
      });
  }

  function merge(lists, cartItems, maxPosition) {
    var byId = {};
    var order = [];

    /* Everything already in the cart is dropped. Dawn does not do this, and it
       is why a cart could suggest the thing sitting directly above it. */
    var inCart = {};
    var totalCartQuantity = 0;
    for (var c = 0; c < cartItems.length; c++) {
      inCart[String(cartItems[c].id)] = true;
      totalCartQuantity += cartItems[c].quantity || 1;
    }

    for (var l = 0; l < lists.length; l++) {
      var source = lists[l];
      var sourceQuantity = source.quantity || 1;

      for (var r = 0; r < source.results.length; r++) {
        var rec = source.results[r];
        if (inCart[rec.id]) continue;

        var invertedPosition = maxPosition + 1 - rec.position;
        var entry = byId[rec.id];

        if (!entry) {
          entry = byId[rec.id] = {
            id: rec.id,
            node: rec.node,
            count: 0,
            totalQuantity: 0,
            invertedSum: 0,
          };
          order.push(entry);
        }

        entry.count += 1;
        entry.totalQuantity += sourceQuantity;
        entry.invertedSum += invertedPosition;
      }
    }

    var totalCartItems = cartItems.length || 1;
    var safeQuantity = totalCartQuantity || 1;

    for (var i = 0; i < order.length; i++) {
      var e = order[i];
      var qNorm = e.totalQuantity / safeQuantity;
      var nNorm = e.count / totalCartItems;
      var pNorm = e.invertedSum / e.count / maxPosition;
      e.score = W_QUANTITY * qNorm + W_FREQUENCY * nNorm + W_POSITION * pNorm;
    }

    order.sort(function (a, b) {
      return b.score - a.score;
    });

    return order;
  }

  function render(el, ranked) {
    var row = el.querySelector('[data-cart-recs-row]');
    if (!row) return;

    var show = parseInt(el.getAttribute('data-show'), 10) || ranked.length;
    var picked = ranked.slice(0, show);

    row.innerHTML = '';
    for (var i = 0; i < picked.length; i++) {
      /* The inner card is appended, not its wrapper. The wrapper only ever
         existed to carry the id and position across the fetch, and leaving it
         in would put a non-card element into a grid whose children are the
         layout's own items. */
      var card = picked[i].node.firstElementChild;
      if (card) row.appendChild(card);
    }

    if (picked.length > 0) el.removeAttribute('hidden');
  }

  /* The products this shopper last looked at, newest first -- written by
     recently-viewed-products.js on every product page. Storage can be blocked
     (private mode, cleared site data), which simply means no seeds. */
  function viewedProductIds() {
    try {
      var parsed = JSON.parse(localStorage.getItem('viewedProducts') || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  /* The empty cart's row. Same scoring as a full cart, with the last three
     viewed products standing in for cart items -- "Picked for you". A new
     visitor has viewed nothing, and a seed can come back with nothing in
     stock, so anything short of two cards falls back to the collection named
     in data-fallback-url ("Bestsellers" by default). */
  function fillEmpty(el) {
    var limit = parseInt(el.getAttribute('data-limit'), 10) || 10;
    var seeds = viewedProductIds()
      .slice(0, 3)
      .map(function (id) {
        return { id: String(id), quantity: 1 };
      });

    var picked = seeds.length
      ? Promise.all(
          seeds.map(function (seed) {
            return fetchFor(el, seed.id, limit).then(function (results) {
              return { quantity: 1, results: results };
            });
          })
        ).then(function (lists) {
          return merge(lists, seeds, limit);
        })
      : Promise.resolve([]);

    picked
      .then(function (ranked) {
        if (ranked.length >= 2) return ranked;

        var title = el.querySelector('[data-cart-recs-title]');
        var fallbackTitle = el.getAttribute('data-title-fallback');
        if (title && fallbackTitle) title.textContent = fallbackTitle;

        var base = el.getAttribute('data-fallback-url') || '/collections/all';
        var sectionId = el.getAttribute('data-section-id');
        var url =
          base +
          (base.indexOf('?') === -1 ? '?' : '&') +
          'sections=' +
          encodeURIComponent(sectionId);

        return fetchCards(url, sectionId);
      })
      .then(function (ranked) {
        render(el, ranked);
      })
      .catch(function () {
        /* Silent, as below: the empty state reads fine without the row. */
      });
  }

  function start(el) {
    if (el.dataset.recsStarted === 'true') return;

    /* The empty cart's row sits in a drawer that is closed on almost every
       page view, and is rendered on every page. Fetching on load would cost
       every visitor several requests for a row few of them see, so it waits
       until the drawer is actually open. The PARENT is watched because the
       row itself is [hidden] -- display: none never intersects. */
    if (el.getAttribute('data-mode') === 'empty') {
      el.dataset.recsStarted = 'true';
      var target = el.parentElement || el;
      if (typeof IntersectionObserver !== 'function') {
        fillEmpty(el);
        return;
      }
      var io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            io.disconnect();
            fillEmpty(el);
            return;
          }
        }
      });
      io.observe(target);
      return;
    }

    el.dataset.recsStarted = 'true';

    var cartItems = parseCartItems(el);
    if (!cartItems.length) return;

    var limit = parseInt(el.getAttribute('data-limit'), 10) || 10;

    var jobs = cartItems.map(function (item) {
      return fetchFor(el, item.id, limit).then(function (results) {
        return { quantity: item.quantity || 1, results: results };
      });
    });

    Promise.all(jobs)
      .then(function (lists) {
        var ranked = merge(lists, cartItems, limit);
        render(el, ranked);
      })
      .catch(function () {
        /* Silent: the row stays hidden and the drawer is unchanged. A cart
           that cannot show suggestions is still a working cart. */
      });
  }

  function scan(root) {
    var els = (root || document).querySelectorAll('cart-recommendations');
    for (var i = 0; i < els.length; i++) start(els[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan();
    });
  } else {
    scan();
  }

  /* The drawer is re-rendered through the Section Rendering API every time the
     cart changes, which replaces this element with a fresh one carrying the
     new cart contents. Without this the recommendations would reflect the cart
     as it was when the page loaded. */
  if (typeof MutationObserver === 'function') {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        if (records[i].addedNodes.length) {
          scan();
          return;
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
